import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { useHistory } from 'react-router-dom';

import { IconButton, ListItemText, Menu, MenuItem } from '@material-ui/core';
import withWidth, { isWidthUp } from '@material-ui/core/withWidth';
import EditIcon from '@material-ui/icons/Edit';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import {
  TEACHERS_ASSIGNMENTS_DETAIL_URL,
  TEACHERS_ASSIGNMENTS_FEEDBACK_URL,
  TEACHER_ASSIGNMENT_V2_INSIGHTS_URL
} from 'config/urls';
import { filter, get, head, includes, isNil, map, some, uniq } from 'lodash';
import {
  useAssignmentStudentWork,
  useResponsesWithAIGradingInProgress
} from 'sdk';
import { copyToClipboard } from 'utils';

import { fillEmptyStudentWork } from 'pages/Teachers/Insights/components/GradeOverview/utils';
import StudentWorkInsightsDialog from 'pages/Teachers/Insights/components/StudentWorkInsightsDialog';
import { fetchStudentAssignmentDetail } from 'pages/Teachers/Insights/components/StudentWorkInsightsDialog/sdk';
import {
  getNextNonGradedStudent,
  getNextNonGradedTask,
  getNextTask,
  getObjectFromListOrFirst,
  getPreviousTask
} from 'pages/Teachers/Insights/components/StudentWorkInsightsDialog/utils';
import { GRADE_TAB } from 'pages/Teachers/Insights/constants';
import { colors } from 'theme/palette';
import { useDialog } from 'utils/hooks';
import { trackMixpanelPageView } from 'utils/integrations/mixpanel';
import { reverse, stringifyParams } from 'utils/urls';

import MultiSelectTooltip from './components/MultiSelectTooltip';
import TrackerInstanceSectionContext from 'components/TrackerInstanceSectionContext';
import UserContext from 'components/UserContext';

import { ReactComponent as MultiSelectIcon } from './assets/multiselect-icon.svg';
import { ReactComponent as ShareIcon } from './assets/share-icon.svg';
import { Filters, PageSkeleton, StudentWorkList } from './components';
import {
  useAccordion,
  useActiveTasksIds,
  useFilters,
  useOrdering,
  usePageTooltips,
  usePulseEffect,
  usePusherManager,
  useVisibleStudentWork
} from './hooks';
import styles from './styles.module.scss';
import {
  addCommentBulk as addCommentBulkUtil,
  addComment as addCommentUtil,
  addStickersBulk as addStickersBulkUtil,
  changeTasksScoresBulk as changeTasksScoresBulkUtil,
  changeTasksStatusesBulk as changeTasksStatusesBulkUtil,
  handleNewStudentWorkWebhookCallback as handleNewStudentWorkWebhookCallbackUtil,
  updateWorkItems
} from './utils';

const GradeOverview = ({
  trackerId,
  width,
  assignment,
  setAssignment,
  loadingAssignment,
  studentDeepLink,
  selectedSectionId,
  defaultWorkId = null,
  defaultTaskId = null,
  defaultStudentId = null,
  isFeedbackOpened = false
}) => {
  const { user } = useContext(UserContext);
  const { class_id, standards } = useContext(TrackerInstanceSectionContext);

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedWorksSelectedChildren, setSelectedWorksSelectedChildren] =
    useState({});
  const [studentWork, setStudentWork] = useState([]);

  const [filters, setFilters] = useFilters();
  const [ordering, setOrdering] = useOrdering();

  const history = useHistory();

  useEffect(() => {
    trackMixpanelPageView(user.id, {
      feature: 'insights',
      view: 'grade',
      class_id,
      standards,
      normalizedURL:
        process.env.REACT_APP_BASE_FRONTEND_URL +
        TEACHER_ASSIGNMENT_V2_INSIGHTS_URL
    });
  }, [user, class_id, standards]);

  const {
    data: studentWorkData,
    isLoading: loadingStudentWork,
    refetch: refetchStudentWork
  } = useAssignmentStudentWork({ assignmentId: selectedSectionId });

  useEffect(() => {
    setStudentWork(fillEmptyStudentWork(studentWorkData));
  }, [studentWorkData]);

  const {
    isOpened: isStudentWorkInsightsDialogOpen,
    openDialog: openStudentWorkInsightsDialog,
    closeDialog: closeStudentWorkInsightsDialog
  } = useDialog();

  const [
    multiSelectTooltipOpen,
    onMultiSelectTooltipClick,
    tutoringTooltipOpen
  ] = usePageTooltips();

  const [activeTasksIds, storeActiveTaskId, removeActiveTaskId] =
    useActiveTasksIds();

  const [
    visibleStudentWork,
    toggleSelectStudentWorkItem,
    toggleSelectAllVisibleStudentWork
  ] = useVisibleStudentWork(
    studentWork,
    filters,
    ordering,
    !multiSelectTooltipOpen && !tutoringTooltipOpen,
    activeTasksIds
  );

  const [pulseEffectActive, startPulseEffect, stopPulseEffect] =
    usePulseEffect();

  const selectedStudentWorkList = useMemo(
    () => filter(visibleStudentWork, 'selected'),
    [visibleStudentWork]
  );

  const studentsWithoutAIFeedbackIds = useMemo(
    () =>
      visibleStudentWork
        .filter(({ student }) => !student.is_ai_graded)
        .map(({ student }) => student.id),
    [visibleStudentWork]
  );

  const selectedStudentWork = useMemo(() => {
    return selectedStudentWorkList
      .map((item) => item.work) // extract 'work' arrays
      .flat() // flatten one level
      .filter((workItem) => workItem.selected); // filter by selected === true
  }, [selectedStudentWorkList]);

  const {
    data: gradedNotGraded,
    isLoading,
    refetch: refetchGradedNotGraded
  } = useResponsesWithAIGradingInProgress({
    trackerInstanceSectionId: selectedSectionId,
    params: { students: studentsWithoutAIFeedbackIds }
  });

  useEffect(() => {
    if (gradedNotGraded?.waiting_feedback?.length > 0) {
      refetchStudentWork();
    }
  }, [gradedNotGraded, refetchStudentWork]);

  const visibleStudentWorkWithGradingStatus = useMemo(() => {
    if (loadingStudentWork) {
      return map(visibleStudentWork, (studentWorkItem) => {
        return {
          ...studentWorkItem,
          waitingForFeedback: true
        };
      });
    }
    if (!isNil(gradedNotGraded)) {
      return map(visibleStudentWork, (studentWorkItem) => {
        const waitingForFeedback = studentWorkItem.work.some((workItem) =>
          gradedNotGraded.waiting_feedback.includes(
            workItem.tracker_score_response_id
          )
        );
        return {
          ...studentWorkItem,
          waitingForFeedback
        };
      });
    }

    return visibleStudentWork;
  }, [visibleStudentWork, gradedNotGraded, loadingStudentWork]);

  const addCommentBulk = useCallback(
    (text) => {
      const newStudentWork = addCommentBulkUtil(
        { text },
        {
          selectedResponses: map(
            selectedStudentWork,
            'tracker_score_response_id'
          ),
          user,
          studentWork
        }
      );
      setStudentWork(newStudentWork);
      startPulseEffect();
    },
    [selectedStudentWork, user, studentWork, setStudentWork, startPulseEffect]
  );

  const updateWorkItemsInState = useCallback(
    (newWorkList) => {
      const newStudentWork = updateWorkItems({ newWorkList }, { studentWork });
      setStudentWork(newStudentWork);
    },
    [studentWork, setStudentWork]
  );

  const addStickersBulk = useCallback(
    (stickerUrl) => {
      addStickersBulkUtil(
        { stickerUrl },
        {
          selectedResponses: map(
            selectedStudentWork,
            'tracker_score_response_id'
          ),
          studentWork,
          updateWorkItemsInState,
          selectedWorksSelectedChildren
        }
      );
    },
    [
      selectedStudentWork,
      studentWork,
      updateWorkItemsInState,
      selectedWorksSelectedChildren
    ]
  );

  const changeTasksStatusesBulk = useCallback(
    (status) => {
      changeTasksStatusesBulkUtil(
        { status },
        {
          selectedResponses: map(
            selectedStudentWork,
            'tracker_score_response_id'
          ),
          selectedSectionId,
          studentWork
        }
      ).then((newStudentWork) => {
        if (newStudentWork) {
          setStudentWork(newStudentWork);
          startPulseEffect();
        }
      });
    },
    [
      selectedStudentWork,
      selectedSectionId,
      studentWork,
      setStudentWork,
      startPulseEffect
    ]
  );

  const changeTasksScoresBulk = useCallback(
    (scoreValue) => {
      changeTasksScoresBulkUtil(
        { scoreValue },
        {
          selectedResponses: map(
            selectedStudentWork,
            'tracker_score_response_id'
          ),
          studentWork,
          trackerScoreResponses: get(assignment, 'tracker_score_responses', [])
        }
      ).then((data) => {
        if (!data) {
          return;
        }
        setStudentWork(data.newStudentWork);
        setAssignment({
          ...assignment,
          tracker_score_responses: data.newTrackerScoreResponses
        });
        startPulseEffect();
      });
    },
    [
      selectedStudentWork,
      studentWork,
      assignment,
      setStudentWork,
      setAssignment,
      startPulseEffect
    ]
  );

  const shouldPollStudentWorks = useMemo(() => {
    if (isLoading) {
      return false;
    }
    if (!isNil(gradedNotGraded) && studentsWithoutAIFeedbackIds) {
      const waitingFeedback = gradedNotGraded.waiting_feedback;
      return waitingFeedback.length !== 0;
    }
    return true;
  }, [gradedNotGraded, isLoading, studentsWithoutAIFeedbackIds]);

  const selectedResponsesValueTypes = useMemo(() => {
    const ids = map(selectedStudentWork, 'tracker_instance_objective_id');
    return map(
      filter(assignment.objectives, (x) => includes(ids, x.id)),
      'value_type'
    );
  }, [selectedStudentWork, assignment.objectives]);

  const responsesValueType = useMemo(
    () => head(selectedResponsesValueTypes),
    [selectedResponsesValueTypes]
  );
  const scoreChangeDisabled = useMemo(
    () => uniq(selectedResponsesValueTypes).length > 1,
    [selectedResponsesValueTypes]
  );

  const statusChangeDisabled = useMemo(
    () => some(map(selectedStudentWork, 'emptyWork')),
    [selectedStudentWork]
  );

  useEffect(() => {
    if (shouldPollStudentWorks) {
      const interval = setInterval(() => refetchGradedNotGraded(), 3000);

      return () => clearInterval(interval);
    }
  }, [shouldPollStudentWorks, refetchGradedNotGraded]);

  const handleNewStudentWorkWebhookCallback = useCallback(
    (newStudentWorkIds) => {
      handleNewStudentWorkWebhookCallbackUtil(
        { newStudentWorkIds },
        { studentWork }
      ).then((newStudentWork) => {
        if (newStudentWork) {
          setStudentWork(newStudentWork);
        }
      });
    },
    [studentWork, setStudentWork]
  );

  usePusherManager(selectedSectionId, handleNewStudentWorkWebhookCallback);

  const [isAccordionOpen] = useAccordion();

  const buildAssignmentFeedbackLinkObject = useCallback(
    ({ student, work, task }) => ({
      pathname: reverse(TEACHERS_ASSIGNMENTS_FEEDBACK_URL, {
        trackerId: trackerId
      }),
      search: stringifyParams({
        section: selectedSectionId,
        student,
        task,
        work
      })
    }),
    [selectedSectionId, trackerId]
  );

  const addComment = useCallback(
    ({ studentId, taskId, data }) => {
      addCommentUtil({ studentId, taskId, data }, { studentWork }).then(
        setStudentWork
      );
    },
    [studentWork, setStudentWork]
  );

  const lgScreen = isWidthUp('lg', width);

  const handleAssignmentEditClick = (event) => {
    event.stopPropagation();
    history.push({
      pathname: reverse(TEACHERS_ASSIGNMENTS_DETAIL_URL, {
        trackerId: trackerId
      })
    });
  };

  const handleDialogClose = () => {
    if (isFeedbackOpened) {
      history.push({
        pathname: reverse(TEACHER_ASSIGNMENT_V2_INSIGHTS_URL, {
          trackerInstanceSectionId: selectedSectionId
        }),
        search: stringifyParams({
          activeTab: GRADE_TAB,
          isFeedbackOpened: false
        })
      });
    } else {
      closeStudentWorkInsightsDialog();
      setExcludedTaskIds([]);
    }
  };

  // FEEDBACK EDITING
  const [activeTask, setActiveTask] = useState(null);
  const [activeWork, setActiveWork] = useState(null);
  const [activeStudentId, setActiveStudentId] = useState(null);
  const [studentAssignment, setStudentAssignment] = useState(null);
  const [excludedTaskIds, setExcludedTaskIds] = useState([]);

  const tasks = useMemo(() => {
    return get(studentAssignment, 'tasks', []);
  }, [studentAssignment]);

  const handleStudentWorkClick = useCallback(
    async ({ studentId, taskId, workId }) => {
      setActiveStudentId(studentId);

      const assignment = await fetchStudentAssignmentDetail({
        assignmentId: selectedSectionId,
        studentId: studentId
      });
      setStudentAssignment(assignment);

      const newTasks = get(assignment, 'tasks', []);
      const newTask = getObjectFromListOrFirst(newTasks, taskId);
      setActiveTask(newTask);

      const newWorks = get(newTask, 'work', []);
      const newWork = getObjectFromListOrFirst(newWorks, workId);
      setActiveWork(newWork);

      openStudentWorkInsightsDialog();
    },
    [
      openStudentWorkInsightsDialog,
      setActiveTask,
      setActiveWork,
      selectedSectionId
    ]
  );

  const refetchAssignment = useCallback(async () => {
    const assignment = await fetchStudentAssignmentDetail({
      assignmentId: selectedSectionId,
      studentId: activeStudentId
    });

    setStudentAssignment(assignment);
    return assignment;
  }, [selectedSectionId, activeStudentId]);

  useEffect(() => {
    if (!activeStudentId) return;
    if (studentAssignment) return;

    refetchAssignment();
  }, [refetchAssignment, studentAssignment, activeStudentId]);

  useEffect(() => {
    if (activeTask) return;

    const selectedTask = getObjectFromListOrFirst(tasks, defaultTaskId);
    setActiveTask(selectedTask);
  }, [activeTask, tasks, defaultTaskId]);

  useEffect(() => {
    if (activeWork) return;

    const work = get(activeTask, 'work', []);
    const selectedWork = getObjectFromListOrFirst(work, defaultWorkId);
    setActiveWork(selectedWork);
  }, [activeTask, activeWork, defaultWorkId]);

  useEffect(() => {
    if (activeStudentId) return;

    setActiveStudentId(defaultStudentId);
  }, [activeStudentId, defaultStudentId]);

  const gotoNextStudent = async () => {
    const nextStudent = getNextNonGradedStudent({
      studentWorks: visibleStudentWorkWithGradingStatus,
      activeStudentId,
      activeTask,
      excludedTaskIds
    });

    if (!nextStudent) {
      refetchStudentWork();
      closeStudentWorkInsightsDialog();
      setExcludedTaskIds([]);
    } else {
      const assignment = await fetchStudentAssignmentDetail({
        assignmentId: selectedSectionId,
        studentId: nextStudent.id
      });
      setStudentAssignment(assignment);
      setActiveStudentId(nextStudent.id);

      const newTasks = get(assignment, 'tasks', []);
      const selectedTask = getNextNonGradedTask({
        tasks: newTasks,
        excludedTaskIds
      });
      setActiveTask(selectedTask);

      const newWorks = get(selectedTask, 'work', []);
      const selectedWork = getObjectFromListOrFirst(newWorks);
      setActiveWork(selectedWork);
    }
  };

  const gotoPrevTask = async () => {
    const assignment = await fetchStudentAssignmentDetail({
      assignmentId: selectedSectionId,
      studentId: activeStudentId
    });
    const assignmentTasks = get(assignment, 'tasks', []);

    const previousTask = getPreviousTask(assignmentTasks, activeTask);
    setActiveTask(previousTask);

    const previousWorks = get(previousTask, 'work', []);
    const previousWork = getObjectFromListOrFirst(previousWorks);
    setActiveWork(previousWork);

    setStudentAssignment(assignment);
  };

  const gotoNextTask = async () => {
    const assignment = await fetchStudentAssignmentDetail({
      assignmentId: selectedSectionId,
      studentId: activeStudentId
    });
    const assignmentTasks = get(assignment, 'tasks', []);

    const nextTask = getNextTask(assignmentTasks, activeTask);
    setActiveTask(nextTask);

    const nextWorks = get(nextTask, 'work', []);
    const nextWork = getObjectFromListOrFirst(nextWorks);
    setActiveWork(nextWork);

    setStudentAssignment(assignment);
  };

  const gotoNextUngradedTask = async () => {
    const assignment = await fetchStudentAssignmentDetail({
      assignmentId: selectedSectionId,
      studentId: activeStudentId
    });
    const assignmentTasks = get(assignment, 'tasks', []);

    setExcludedTaskIds((prev) => [...prev, activeTask.id]);

    const nextTask = getNextNonGradedTask({
      tasks: assignmentTasks,
      activeTask,
      excludedTaskIds
    });

    if (!nextTask) {
      gotoNextStudent();
    } else {
      setActiveTask(nextTask);
      const nextWorks = get(nextTask, 'work', []);
      const nextWork = getObjectFromListOrFirst(nextWorks);
      setActiveWork(nextWork);
      setStudentAssignment(assignment);
    }
  };

  const studentName = useMemo(() => {
    const studentWorks = visibleStudentWorkWithGradingStatus;

    const studentItemIndex = studentWorks.findIndex(
      (sw) => sw.student.id === activeStudentId
    );

    const studentItem = studentWorks[studentItemIndex];

    return get(studentItem, 'student.name', '');
  }, [activeStudentId, visibleStudentWorkWithGradingStatus]);

  if (loadingStudentWork || loadingAssignment || isNil(assignment)) {
    return <PageSkeleton lgScreen={lgScreen} />;
  }

  return (
    <>
      <div className={styles.headerContainer}>
        <Filters
          filters={filters}
          ordering={ordering}
          setFilters={setFilters}
          setOrdering={setOrdering}
          selectedSectionId={selectedSectionId}
          studentWork={studentWork}
        />

        <div className={styles.buttonsContainer}>
          <div>
            <IconButton
              aria-label="more"
              aria-controls="cabap-menu"
              aria-haspopup="true"
              onClick={(event) => setAnchorEl(event.currentTarget)}
            >
              <MoreVertIcon />
            </IconButton>

            <Menu
              id="cabap-menu"
              anchorEl={anchorEl}
              keepMounted
              open={Boolean(anchorEl)}
              onClose={() => setAnchorEl(null)}
            >
              <MenuItem onClick={handleAssignmentEditClick}>
                <EditIcon
                  fontSize="small"
                  style={{ color: colors.blue3, marginRight: '10px' }}
                />

                <ListItemText primary="Edit Assignment" />
              </MenuItem>
              <MenuItem onClick={() => copyToClipboard(studentDeepLink)}>
                <ShareIcon fontSize="small" style={{ marginRight: '10px' }} />

                <ListItemText primary="Share to students" />
              </MenuItem>

              <MenuItem
                onClick={() => {
                  setAnchorEl(null);
                  onMultiSelectTooltipClick();
                }}
              >
                <MultiSelectIcon
                  fontSize="small"
                  style={{
                    marginRight: '10px',
                    width: '20px',
                    height: '20px',
                    stroke: colors.blue3
                  }}
                />
                <ListItemText primary="Select Multiple" />
              </MenuItem>
            </Menu>
          </div>

          <MultiSelectTooltip
            open={multiSelectTooltipOpen}
            handleClose={onMultiSelectTooltipClick}
            addCommentBulk={addCommentBulk}
            addStickersBulk={addStickersBulk}
            changeTasksStatusesBulk={changeTasksStatusesBulk}
            toggleSelectAllVisibleStudentWork={
              toggleSelectAllVisibleStudentWork
            }
            changeTasksScoresBulk={changeTasksScoresBulk}
            scoreChangeDisabled={scoreChangeDisabled}
            responsesValueType={responsesValueType}
            statusChangeDisabled={statusChangeDisabled}
          >
            <div />
          </MultiSelectTooltip>
        </div>
      </div>

      <StudentWorkList
        studentWorkList={visibleStudentWorkWithGradingStatus}
        filters={filters}
        multiSelectMode={multiSelectTooltipOpen || tutoringTooltipOpen}
        buildAssignmentFeedbackLinkObject={buildAssignmentFeedbackLinkObject}
        showScores={isAccordionOpen}
        addComment={addComment}
        pulseEffectTriggered={pulseEffectActive}
        onPulseEffectEnd={stopPulseEffect}
        toggleSelectStudentWorkItem={toggleSelectStudentWorkItem}
        activeTasksIds={activeTasksIds}
        storeActiveTaskId={storeActiveTaskId}
        removeActiveTaskId={removeActiveTaskId}
        setSelectedWorksSelectedChildren={setSelectedWorksSelectedChildren}
        refetchStudentWork={refetchStudentWork}
        handleStudentWorkClick={handleStudentWorkClick}
      />

      {(isStudentWorkInsightsDialogOpen || isFeedbackOpened) &&
        activeTask &&
        activeWork && (
          <StudentWorkInsightsDialog
            activeTask={activeTask}
            activeWork={activeWork}
            setActiveTask={setActiveTask}
            setActiveWork={setActiveWork}
            studentName={studentName}
            onClose={handleDialogClose}
            refetchStudentWork={refetchStudentWork}
            refetchAssignment={refetchAssignment}
            gotoPrevTask={gotoPrevTask}
            gotoNextTask={gotoNextTask}
            gotoNextUngradedTask={gotoNextUngradedTask}
          />
        )}
    </>
  );
};

export default withWidth()(GradeOverview);
